import React, { useEffect, useRef, useState } from 'react';
import { Scenario, Scene } from '../../types';
import { AssetField } from './AssetPickerModal';
import { JsonEditor } from './JsonEditor';
import { initializeObservationScene } from './ObjectInspectEditor';
import { getSceneSummary, uniqueId } from './utils';
import styles from './scenario-studio.module.css';

interface SceneListProps {
  scenarioId: string;
  scenario: Scenario;
  selectedSceneId: string | null;
  onSelectScene: (id: string) => void;
  onChange: (scenario: Scenario) => void;
}

export const SceneList: React.FC<SceneListProps> = ({
  scenarioId,
  scenario,
  selectedSceneId,
  onSelectScene,
  onChange,
}) => {
  const addScene = () => {
    const id = uniqueId('scene');
    const scene: Scene = { id, type: 'dialogue', background: '', text: '' };
    onChange({ ...scenario, scenes: [...scenario.scenes, scene] });
    onSelectScene(id);
  };

  const removeScene = (id: string) => {
    if (!window.confirm(`Supprimer la scène ${id} ?`)) return;
    onChange({ ...scenario, scenes: scenario.scenes.filter((s) => s.id !== id) });
  };

  const move = (id: string, dir: -1 | 1) => {
    const scenes = [...scenario.scenes];
    const idx = scenes.findIndex((s) => s.id === id);
    const newIdx = idx + dir;
    if (idx < 0 || newIdx < 0 || newIdx >= scenes.length) return;
    const [item] = scenes.splice(idx, 1);
    scenes.splice(newIdx, 0, item!);
    onChange({ ...scenario, scenes });
  };

  const updateScene = (id: string, patch: Partial<Scene>) => {
    onChange({
      ...scenario,
      scenes: scenario.scenes.map((s) => (s.id === id ? ({ ...s, ...patch } as Scene) : s)),
    });
  };

  const selected = scenario.scenes.find((s) => s.id === selectedSceneId);
  const [query, setQuery] = useState('');
  const activeSceneRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    activeSceneRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedSceneId]);
  const normalizedQuery = query.trim().toLowerCase();
  const filteredScenes = normalizedQuery
    ? scenario.scenes.filter((s) => s.id.toLowerCase().includes(normalizedQuery))
    : scenario.scenes;

  return (
    <div className={styles.sceneList}>
      <div className={styles.panelHeader}>
        <h3>Scènes ({scenario.scenes.length})</h3>
        <button type="button" className={styles.btnSecondary} onClick={addScene} title="Ajouter une scène">+ Scène</button>
      </div>
      <div className={styles.searchBox}>
        <span className={styles.searchIcon} aria-hidden>🔍</span>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Filtrer les scènes…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <ul className={styles.sceneNav}>
        {filteredScenes.map((scene) => (
          <li key={scene.id}>
            <button
              type="button"
              ref={selectedSceneId === scene.id ? activeSceneRef : undefined}
              className={`${styles.sceneNavItem} ${selectedSceneId === scene.id ? styles.active : ''}`}
              onClick={() => onSelectScene(scene.id)}
              title={scene.id}
            >
              <span className={styles.sceneNavTitle}>
                {scene.id}
                {scenario.metadata.startScene === scene.id && (
                  <span className={styles.startBadge} title="Scène de départ">DÉPART</span>
                )}
              </span>
              <span className={styles.sceneNavMeta}>{getSceneSummary(scene)}</span>
            </button>
            <div className={styles.sceneNavActions}>
              <button type="button" onClick={() => move(scene.id, -1)} title="Monter">↑</button>
              <button type="button" onClick={() => move(scene.id, 1)} title="Descendre">↓</button>
              <button type="button" onClick={() => removeScene(scene.id)} title="Supprimer">×</button>
            </div>
          </li>
        ))}
        {filteredScenes.length === 0 && (
          <li><p className={styles.noResults}>Aucune scène ne correspond.</p></li>
        )}
      </ul>

      {selected && (
        <div className={styles.panel}>
          <h4>Propriétés scène</h4>
          <div className={styles.field}>
            <label>ID</label>
            <input
              className={styles.input}
              value={selected.id}
              onChange={(e) => {
                const newId = e.target.value;
                onChange({
                  ...scenario,
                  scenes: scenario.scenes.map((s) =>
                    s.id === selected.id ? { ...s, id: newId } : s
                  ),
                  metadata: {
                    ...scenario.metadata,
                    startScene:
                      scenario.metadata.startScene === selected.id
                        ? newId
                        : scenario.metadata.startScene,
                  },
                });
                onSelectScene(newId);
              }}
            />
          </div>
          <AssetField
            scenarioId={scenarioId}
            label="Background"
            value={selected.background ?? ''}
            subdir="backgrounds"
            onChange={(background) => updateScene(selected.id, { background })}
          />
          <div className={styles.field}>
            <label>
              <input
                type="checkbox"
                checked={selected.type === 'observation'}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  if (enabled) {
                    onChange({
                      ...scenario,
                      scenes: scenario.scenes.map((s) =>
                        s.id === selected.id
                          ? initializeObservationScene({ ...s, type: 'observation' } as Scene)
                          : s
                      ),
                    });
                  } else {
                    updateScene(selected.id, { type: 'dialogue', text: '' } as Partial<Scene>);
                  }
                }}
              />
              {' '}Scène d&apos;observation (objets à examiner)
            </label>
          </div>
          {selected.type !== 'observation' && (
            <JsonEditor label="scène complète" value={selected} onChange={(scene) => updateScene(selected.id, scene)} />
          )}
        </div>
      )}
    </div>
  );
};
